import React, { useState, useEffect, useCallback } from 'react';
// Reuses PG's certgen- styling (frontend/src/components/TeacherDashboard/CertificateGeneration.css)
// rather than duplicating it — same cross-folder import pattern UGTeacherReview.js
// already uses for ManageTeacherAccount.
import '../TeacherDashboard/CertificateGeneration.css';
import collegeConfig from '../../config/collegeConfig';
import { jsPDF } from 'jspdf';
import { savePDF } from '../../utils/savePDF';
import {
  FaAward,
  FaUserGraduate,
  FaCheckCircle,
  FaDownload,
  FaRedo,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaSpinner
} from 'react-icons/fa';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// ─── UG E-Logbook Completion Certificate PDF Generator ─────────────────────
// Same landscape layout as PG's generateElogbookCertificatePDF (see
// CertificateGeneration.js) — wording swapped for an MBBS/UG student and a
// single "Faculty In-Charge" + "Issued By" signature pair, since UG has no
// per-department Guide/HOD structure to sign off instead.
const generateUGElogbookCertificatePDF = async (cert, collegeName, logoSrc) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  const MAROON  = [142, 44,  44];
  const GOLD    = [212, 177, 90];
  const NAVY    = [31,  60,  136];
  const DARKGRAY= [60,  60,  60];

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(3);
  doc.rect(8, 8, W - 16, H - 16);
  doc.setLineWidth(1);
  doc.rect(12, 12, W - 24, H - 24);

  doc.setFillColor(...MAROON);
  doc.rect(12, 12, W - 24, 52, 'F');

  const logoBase64 = await getBase64Image(logoSrc);
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', W / 2 - 14, 13, 28, 28); }
    catch (_) { /* skip */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const nameLines = doc.splitTextToSize(collegeName, W - 60);
  doc.text(nameLines, W / 2, 48, { align: 'center' });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(1.2);
  doc.line(15, 66, W - 15, 66);

  doc.setTextColor(...MAROON);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.text('CERTIFICATE', W / 2, 82, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(12);
  doc.setTextColor(...GOLD);
  doc.text('of E-Logbook Completion', W / 2, 90, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text('MBBS (Undergraduate)', W / 2, 99, { align: 'center' });

  const batchLabel = cert.batchLabel || '____';

  const bodyText =
    `This is to certify that ${cert.studentName}, Undergraduate (MBBS) student, Batch ${batchLabel}, at ${collegeName}, ` +
    `has successfully and satisfactorily maintained and completed the E-Logbook covering all academic activities, clinical ` +
    `postings, procedures, and case discussions as mandated by the NMC CBME curriculum. The entries recorded therein have ` +
    `been duly reviewed and verified by the concerned faculty.`;

  doc.setFont('times', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...DARKGRAY);
  const bodyLines = doc.splitTextToSize(bodyText, W - 60);
  doc.text(bodyLines, W / 2, 111, { align: 'center', lineHeightFactor: 1.6 });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(15, H - 52, W - 15, H - 52);

  const col1 = 50;
  const col2 = W / 2;
  const col3 = W - 50;
  const sigY = H - 32;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Faculty In-Charge', col1, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col1 - 30, sigY - 4, col1 + 30, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.facultyInchargeName || '', col1, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Date', col2, sigY - 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(formatDate(cert.generatedAt), col2, sigY, { align: 'center' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...MAROON);
  doc.text('Issued By', col3, sigY - 12, { align: 'center' });
  doc.setDrawColor(...DARKGRAY);
  doc.setLineWidth(0.4);
  doc.line(col3 - 35, sigY - 4, col3 + 35, sigY - 4);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...DARKGRAY);
  doc.text(cert.issuedByName || '', col3, sigY, { align: 'center' });

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(`Certificate No: ${cert.certificateNumber || ''}`, W / 2, H - 16, { align: 'center' });

  await savePDF(doc, `UG_Elogbook_Certificate_${cert.studentName?.replace(/\s+/g, '_')}.pdf`);
};

// ─── Component ────────────────────────────────────────────────────────────────
const UGCertificateGeneration = () => {
  const [batches, setBatches] = useState({});
  const [ugBatches, setUgBatches] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [search, setSearch] = useState('');
  const [studentFaculty, setStudentFaculty] = useState({}); // { studentId: facultyId }
  const [generating, setGenerating] = useState({}); // { studentId: true/false }
  const [downloadingId, setDownloadingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [studentsRes, facultyRes] = await Promise.all([
        fetch(`${collegeConfig.api.baseURL}/ug/certificates/students`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${collegeConfig.api.baseURL}/ug/certificates/faculty`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const studentsData = await studentsRes.json();
      const facultyData = await facultyRes.json();

      if (studentsData.success) {
        setBatches(studentsData.batches || {});
        setUgBatches(studentsData.ugBatches || []);
        const firstBatch = Object.keys(studentsData.batches || {})[0];
        if (firstBatch) setExpandedBatch(firstBatch);
      } else {
        setError(studentsData.message || 'Failed to load students');
      }

      if (facultyData.success) {
        setFaculty(facultyData.teachers || []);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async (student) => {
    const facultyInchargeId = studentFaculty[student._id] || '';
    setGenerating(prev => ({ ...prev, [student._id]: true }));
    setError('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/certificates/elogbook/generate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studentId: student._id, facultyInchargeId })
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`E-Logbook certificate generated for ${student.name}!`);
        setTimeout(() => setSuccessMsg(''), 4000);
        await fetchData();
      } else {
        setError(data.message || 'Failed to generate E-Logbook certificate');
      }
    } catch (err) {
      setError('Network error while generating E-Logbook certificate.');
    } finally {
      setGenerating(prev => ({ ...prev, [student._id]: false }));
    }
  };

  const handleDownload = async (student) => {
    setDownloadingId(student.elogbookCertificateId || student._id);
    try {
      await generateUGElogbookCertificatePDF(
        student.elogbookCertData || {
          studentName: student.name,
          certificateNumber: '',
          generatedAt: new Date().toISOString()
        },
        collegeConfig.collegeName,
        collegeConfig.logo
      );
    } finally {
      setDownloadingId(null);
    }
  };

  // Filter students by search
  const filteredBatches = {};
  for (const [batch, students] of Object.entries(batches)) {
    const filtered = students.filter(s =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.registrationNumber || '').toLowerCase().includes(search.toLowerCase())
    );
    if (filtered.length > 0) filteredBatches[batch] = filtered;
  }

  // Newest batch first (ugBatches already sorted by admissionYear desc),
  // "Unassigned" always last.
  const orderedBatchLabels = [
    ...ugBatches.map(b => b.batchLabel).filter(l => filteredBatches[l]),
    ...(filteredBatches['Unassigned'] ? ['Unassigned'] : [])
  ];

  const totalStudents = Object.values(batches).flat().length;
  const certifiedCount = Object.values(batches).flat().filter(s => s.hasElogbookCertificate).length;

  if (loading) {
    return (
      <div className="certgen-loading">
        <FaSpinner className="certgen-spinner" />
        <p>Loading certificate data...</p>
      </div>
    );
  }

  return (
    <div className="certgen-container">
      <div className="certgen-header">
        <div className="certgen-header-icon">
          <FaAward />
        </div>
        <div>
          <h2 className="certgen-title">E-Logbook Certificate Generation</h2>
          <p className="certgen-subtitle">MBBS (Undergraduate)</p>
        </div>
      </div>

      <div className="certgen-stats">
        <div className="certgen-stat-card">
          <span className="certgen-stat-num">{totalStudents}</span>
          <span className="certgen-stat-label">Total Students</span>
        </div>
        <div className="certgen-stat-card certgen-stat-green">
          <span className="certgen-stat-num">{certifiedCount}</span>
          <span className="certgen-stat-label">Certificates Generated</span>
        </div>
        <div className="certgen-stat-card certgen-stat-amber">
          <span className="certgen-stat-num">{totalStudents - certifiedCount}</span>
          <span className="certgen-stat-label">Pending</span>
        </div>
      </div>

      {successMsg && (
        <div className="certgen-success-banner">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {error && (
        <div className="certgen-error-banner">{error}</div>
      )}

      <div className="certgen-search-bar">
        <FaSearch className="certgen-search-icon" />
        <input
          type="text"
          placeholder="Search student by name or reg. number..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="certgen-search-input"
        />
      </div>

      {orderedBatchLabels.length === 0 ? (
        <div className="certgen-empty">
          <FaUserGraduate size={48} />
          <p>No students found</p>
        </div>
      ) : (
        orderedBatchLabels.map(batch => {
          const students = filteredBatches[batch];
          return (
            <div key={batch} className="certgen-batch-card">
              <button
                className="certgen-batch-header"
                onClick={() => setExpandedBatch(expandedBatch === batch ? null : batch)}
              >
                <span className="certgen-batch-name">Batch: {batch}</span>
                <span className="certgen-batch-meta">
                  {students.filter(s => s.hasElogbookCertificate).length}/{students.length} certified
                </span>
                {expandedBatch === batch ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              {expandedBatch === batch && (
                <div className="certgen-student-list">
                  {students.map(student => (
                    <div key={student._id} className="certgen-student-row">
                      <div className="certgen-student-info">
                        <FaUserGraduate className="certgen-student-icon" />
                        <div>
                          <p className="certgen-student-name">{student.name}</p>
                          <p className="certgen-student-reg">{student.registrationNumber || '—'}</p>
                        </div>
                        {student.hasElogbookCertificate && (
                          <span className="certgen-badge-generated">
                            <FaCheckCircle /> Generated
                          </span>
                        )}
                      </div>

                      <div className="certgen-student-actions">
                        <select
                          className="certgen-guide-select"
                          value={studentFaculty[student._id] || ''}
                          onChange={e =>
                            setStudentFaculty(prev => ({ ...prev, [student._id]: e.target.value }))
                          }
                        >
                          <option value="">Select Faculty In-Charge (optional)</option>
                          {faculty.map(f => (
                            <option key={f._id} value={f._id}>{f.name}</option>
                          ))}
                        </select>

                        <button
                          className={`certgen-btn ${student.hasElogbookCertificate ? 'certgen-btn-regen' : 'certgen-btn-generate'}`}
                          onClick={() => handleGenerate(student)}
                          disabled={!!generating[student._id]}
                        >
                          {generating[student._id] ? (
                            <FaSpinner className="certgen-btn-spinner" />
                          ) : student.hasElogbookCertificate ? (
                            <><FaRedo /> Regenerate</>
                          ) : (
                            <><FaAward /> Generate</>
                          )}
                        </button>

                        {student.hasElogbookCertificate && (
                          <button
                            className="certgen-btn certgen-btn-download"
                            onClick={() => handleDownload(student)}
                            disabled={downloadingId === student.elogbookCertificateId || downloadingId === student._id}
                          >
                            {downloadingId === student.elogbookCertificateId ? (
                              <FaSpinner className="certgen-btn-spinner" />
                            ) : (
                              <><FaDownload /> PDF</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default UGCertificateGeneration;
